
import { Component, Prop, Vue } from 'bits/vue'
import OzRichDesktopModal from 'library/v4/components/-oz-rich-desktop-modal.vue'
import OzRichMobileModal from 'library/v4/components/-oz-rich-mobile-modal.vue'
import OzRichPopoverModal, { calculatePopoverAnchor } from 'library/v4/components/-oz-rich-popover-modal.vue'
export { calculatePopoverAnchor }

/**
 * Recommended to use OzScreenSizeAwarePopoverModal instead.
 */

@Component({ components: { OzRichDesktopModal, OzRichMobileModal, OzRichPopoverModal } })
export default class OzRichModal extends Vue {
  /**
   * Determines which type of modal to load.
   */
  @Prop({ required: true }) isPhone!: boolean
  /**
   * Window height, required for computing maximum loading height for mobile
   */
  @Prop({ default: window.innerHeight }) windowHeight!: number
  /**
   * Window width, required to check if the modal goes out of bound in popover mode
   */
  @Prop({ default: window.innerWidth }) windowWidth!: number
  /**
   * When this is `true`, modal will have a small indicator for dragging in mobile
   */
  @Prop({ default: false }) xTopLine!: boolean
  /**
   * When this is `true`, modal will be full-screen for mobile and fully expanded vertically for desktop
   */
  @Prop({ default: false }) fullSize!: boolean
  /**
   * When this is `true`, modal will not move when dragging. Other interactions (scrolling) will stay the same
   */
  @Prop({ default: false }) disableDrag!: boolean
  /**
   * Mobile scrolling type.
   * `body` means the header will not scroll.
   * `whole` means the whole modal will scroll together/
   */
  @Prop({ default: 'body' }) scrollType!: 'body' | 'whole'
  /**
   * Mobile preferred start height. If device is too small we self compute this
   */
  @Prop({ default: Infinity }) preferredTrayStartHeight!: number
  /**
   * Desktop modal minimum height
   */
  @Prop({ default: 0 }) desktopViewMinHeight!: number
  /**
   * Starting click location relative to window. For desktop to achieve zoom from click animation.
   * Its type is `{ top: number; left: number } | undefined`. See example for usage
   */
  @Prop({
    default: undefined,
    validator: (val: { top: number; left: number } | undefined): boolean => {
      return val === undefined || (typeof val.top == 'number' && typeof val.left == 'number')
    },
  })
  startingClickLocation!: { top: number; left: number } | undefined

  /**
   * Determines if the modal should be shown as a popover on tablet/desktop
   */
  @Prop({ default: false }) shouldShowPopover!: boolean
  /**
   * Determines the width in popover mode
   */
  @Prop({ default: 'default' }) popoverWidth!: 'default' | 'wide'
  /**
   * Position of the popover's anchor element in popover mode
   * Its type is { position: 'top' | 'side', top: number; left: number } | undefined. See example for usage
   */
  @Prop() popoverAnchor!: { position: 'top' | 'side'; top: number; left: number } | undefined

  /**
   * Determines if the modal should always be in desktop mode (a modal in the middle of the screen)
   */
  @Prop({ default: false }) alwaysDesktopMode!: boolean

  /**
   *  Tailwindcss width class for desktop mode.
   *  Only used in `-oz-rich-desktop-modal`.
   *  `w-180` is for the content picker
   *  `w-128` is for the reaction edit modal
   */
  @Prop({ default: 'w-180' }) desktopModeWidthClass!: 'w-180' | 'w-128'

  _handleEscape: any

  /**
   *  `-oz-rich-desktop-modal` will be displayed in desktop and
   *  also in mobile if `alwaysDesktopMode` is true.
   */
  get modalTag(): string {
    if (this.isPhone && !this.alwaysDesktopMode) {
      return 'oz-rich-mobile-modal'
    } else if (this.shouldShowPopover) {
      return 'oz-rich-popover-modal'
    }

    return 'oz-rich-desktop-modal'
  }

  cancel(event: string) {
    /**
     * Cancel event triggered when clicking outside or esc
     * @property {string} event `'esc'` or `'click'` depends on what triggered cancelling
     */
    this.$emit('cancel', event)
  }

  mounted(): void {
    this._handleEscape = (e: KeyboardEvent): void => {
      if (e.key == 'Escape') {
        e.preventDefault()
        this.cancel('esc')
      }
    }
    window.addEventListener('keydown', this._handleEscape)
  }

  beforeDestroy(): void {
    window.removeEventListener('keydown', this._handleEscape)
  }
}
